import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface ThemeContextProps {
  isDay: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

function applyTheme(isDay: boolean) {
  document.documentElement.setAttribute('data-theme', isDay ? 'light' : 'dark');
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const storedTheme = localStorage.getItem('themeMode');
  const initialTheme = storedTheme ? JSON.parse(storedTheme) : true;

  const [isDay, setIsDay] = useState<boolean>(initialTheme);

  useEffect(() => {
    applyTheme(isDay);
  }, [isDay]);

  // Apply on first render before paint
  applyTheme(initialTheme);

  const toggleTheme = () => {
    const newMode = !isDay;
    setIsDay(newMode);
    localStorage.setItem('themeMode', JSON.stringify(newMode));
  };

  return (
    <ThemeContext.Provider value={{ isDay, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  // Fall back to day mode when rendered outside a ThemeProvider (e.g. a chart
  // mounted in isolation) instead of crashing the subtree.
  if (!context) {
    return { isDay: true, toggleTheme: () => {} };
  }
  return context;
};
